/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 11:29:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Collapse } from 'react-collapse';
import { Icon, Popover, ActionList } from '@shopify/polaris';
import classnames from 'classnames';
import {
  CaretDownMinor,
  CirclePlusMajor,
  ViewMinor,
  HideMinor,
  DragHandleMinor,
  DeleteMinor
} from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import MenuItemArea from './menu-item-area';
import { reorder, reorderTwoList } from 'src/helpers';
import { MENU_ID } from 'src/constants/theme-settings.jsx';
import { MAX_ELEMENT_PER_AREA, BUTTON_TYPE_OPTIONS, ELEMENTS } from 'src/constants/theme-settings';

type State = {
  isOpenCollapse: boolean,
  isOpenMenuAdd: boolean,
  filteredElements: any[]
};

type Props = {
  id: string,
  title: string,
  icon: any,
  isCollapsible: boolean,
  isAddable: boolean,
  activeMenuItem: string,
  elements: any[],
  updateType: string,
  children: any[],
  isHidden?: boolean,
  menus?: any[],
  menuCollection: any[],
  updateField: Function,
  formValues: any,
  notDragDrop: boolean,
  handleChangeActiveMenuItem: (id: string, updateType: string) => void,
  handleUpdateMenuSetting: (id: string, children: any[], updateType: string) => void,
  handleToggleHideMenuItem?: (id: string, updateType: string) => void,
  upsertElementActions: (type: string, properties: Record<string, any>) => void
};

type ComposedProps = Props & WithI18nProps;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  ...draggableStyle,
  background: isDragging ? '#f6f6f7' : ''
});
const _dragEl = document.getElementById('draggable');

// const CLICK_IFRAME_EVENT = 'clickIframe';

class MenuItem extends Component<ComposedProps, State> {
  state = {
    isOpenCollapse: true,
    isOpenMenuAdd: false,
    filteredElements: [],
    isDragging: false,
    isDraggingProductInfo: false
  };

  optionalPortal = (styles, element) => {
    if (styles.position === 'fixed') {
      return createPortal(element, _dragEl);
    }
    return element;
  };

  componentDidMount() {
    const { children } = this.props;
    this.handleFilterElements(children, this.props);
    // Emitter.on(CLICK_IFRAME_EVENT, this.handleClickIframe);
  }

  componentWillReceiveProps(nextProps) {
    const { children } = this.props;
    const { children: nextChildren } = nextProps;
    if (children !== nextChildren) {
      this.handleFilterElements(nextChildren, nextProps);
    }
  }

  // componentWillUnmount() {
  //   Emitter.off(CLICK_IFRAME_EVENT, this.handleClickIframe);
  // }

  // handleClickIframe = () => {
  //   if (this.state.isOpenMenuAdd) {
  //     this.setState({
  //       isOpenMenuAdd: false
  //     });
  //   }
  // };

  handleFilterElements = (children, props) => {
    const { elements, id, menus, menuCollection } = props;

    let restChildren = [];
    // Get rest children of product image or product information in contrary
    if ((id === MENU_ID.PRODUCT_IMAGE || id === MENU_ID.PRODUCT_INFORMATION) && !_.isEmpty(menus)) {
      const mapRestChildren = {
        [MENU_ID.PRODUCT_IMAGE]: () =>
          menus.find(item => item.id === MENU_ID.PRODUCT_INFORMATION)?.children || [],
        [MENU_ID.PRODUCT_INFORMATION]: () => {
          const children = menus.find(item => item.id === MENU_ID.PRODUCT_IMAGE)?.children || [];
          const result = [];
          for (let child of children) {
            (child?.children || []).forEach(grandChild => result.push(grandChild));
          }
          return result;
        }
      };
      restChildren = mapRestChildren[id]();
    }

    if ((id === MENU_ID.COLLECTION_HEADER || id === MENU_ID.TOOLBAR) && !_.isEmpty(menus)) {
      const mapRestChildren = {
        [MENU_ID.COLLECTION_HEADER]: () =>
          menus.find(item => item.id === MENU_ID.TOOLBAR)?.children || [],
        [MENU_ID.TOOLBAR]: () =>
          menuCollection.find(item => item.id === MENU_ID.COLLECTION_HEADER)?.children || []
      };
      restChildren = mapRestChildren[id]();
    }

    // Filter element
    this.setState({
      filteredElements: _.reject(
        elements,
        element =>
          !!children.find(item => element.value === item.value) ||
          !!restChildren.find(item => {
            if (
              // Allow add product count on both header and toolbar
              item?.value === ELEMENTS.COLLECTION_HEADER_PRODUCT_COUNT ||
              item?.value === ELEMENTS.TOOLBAR_PRODUCT_COUNT
            ) {
              return false;
            }
            const [, menuId] = item?.value.split('_');
            const [, elementId] = element?.value.split('_');
            return menuId === elementId;
          })
      )
    });
  };

  handleSelectMenuItem = (id: string) => {
    const { activeMenuItem, updateType, handleChangeActiveMenuItem } = this.props;
    if (id !== activeMenuItem) {
      handleChangeActiveMenuItem(id, updateType);
    }
  };

  handleToggleCollapse = () => {
    this.setState({
      isOpenCollapse: !this.state.isOpenCollapse
    });
  };

  handleTogglePopover = () => {
    this.setState({
      isOpenMenuAdd: !this.state.isOpenMenuAdd
    });
  };

  handleAddElement = element => {
    const {
      i18n,
      id,
      children,
      updateType,
      updateField,
      themeSettingDefaultValue,
      themeSettingDefaultThemeValue,
      handleUpdateMenuSetting,
      upsertElementActions
    } = this.props;
    const { filteredElements } = this.state;
    this.handleTogglePopover();

    const newChildren = [
      ...children,
      {
        ...element,
        label: i18n.translate(element.label),
        isHidden: false,
        position: children.length
      }
    ];

    // Update redux form
    // If default value of match theme does not exist, take the default theme value one
    let setting = {};
    let keySetting = element?.value;

    // If adding a button, it will be set a buttonType already with the priority below
    // 1. selectOptionBtn
    // 2. qvBtn
    // 3. goToDetailBtn
    if (keySetting.includes('button')) {
      // Keys are: selectOptionBtn | qvBtn | goToDetailBtn
      const buttonKeySetting = this.getButtonKeySetting();
      keySetting = buttonKeySetting;
    }

    if (themeSettingDefaultValue && themeSettingDefaultValue[keySetting]) {
      setting = themeSettingDefaultValue[keySetting] || {};
    } else {
      setting = themeSettingDefaultThemeValue[keySetting] || {};
    }

    updateField(element.value, {
      isHidden: false,
      ...setting,
      position: newChildren.length - 1
    });

    // Update menu in reducer
    handleUpdateMenuSetting(id, newChildren, updateType);

    // Tracking event
    const elements = newChildren?.[newChildren?.length - 1]?.value?.split('_');
    upsertElementActions('add', {
      feature: 'Theme Settings',
      selectedTheme: this.props.themeName,
      page: elements[0],
      elementName: elements[0] + '_' + elements[1]
    });
    // mixpanelV3Helper.trackEventData('Add visual element', {
    //   feature: 'Theme Settings',
    //   selectedTheme: this.props.themeName,
    //   page: elements[0],
    //   elementName: elements[0] + '-' + elements[1]
    // });

    setTimeout(() => {
      this.setState({
        filteredElements: _.reject(filteredElements, element =>
          newChildren.find(item => element.value === item.value)
        )
      });
    }, 200);
  };

  getButtonKeySetting = () => {
    const { formValues } = this.props;
    const [productInfoPrefix] = MENU_ID.PRODUCT_INFORMATION.split('_');
    const buttonKeys = Object.keys(formValues).filter(key => key.includes('button'));
    const buttonSettings = buttonKeys.map(key => formValues[key]);
    const availableButtonTypes = BUTTON_TYPE_OPTIONS.filter(option => {
      return !buttonSettings.find(setting => setting.buttonType === option.value);
    });
    return !!availableButtonTypes.length
      ? `${productInfoPrefix}_${availableButtonTypes[0].value}`
      : '';
  };

  onDragStart = () => {
    this.toggleDragging('isDragging', true);
  };

  onDragStartProductInfo = () => {
    this.toggleDragging('isDraggingProductInfo', true);
  };

  onDragEnd = result => {
    this.toggleDragging('isDraggingProductInfo', false);

    if (!_.isNumber(result?.source?.index) || !_.isNumber(result?.destination?.index)) {
      return;
    }
    if (result?.source?.index === result?.destination?.index) {
      return;
    }
    const { id, updateType, children, updateField, handleUpdateMenuSetting } = this.props;
    const reorderedChildren = reorder(children, result.source.index, result.destination.index);
    reorderedChildren.forEach((child, index) => {
      updateField(`${child.value}.position`, index);
    });
    handleUpdateMenuSetting(id, reorderedChildren, updateType);
  };

  onDragEndMultiList = result => {
    this.toggleDragging('isDragging', false);

    if (!_.isNumber(result?.source?.index) || !_.isNumber(result?.destination?.index)) {
      return;
    }
    if (
      result?.source?.index === result?.destination?.index &&
      result?.source?.droppableId === result?.destination?.droppableId
    ) {
      return;
    }

    const {
      updateType,
      menus,
      handleUpdateMenuSetting,
      handleChangeActiveMenuItem,
      updateField,
      formValues,
      activeMenuItem
    } = this.props;
    if (result?.source?.droppableId === result?.destination?.droppableId) {
      const id = result?.source?.droppableId;
      const children = menus.find(item => item.id === id)?.children || [];
      const reorderedChildren = reorder(children, result.source.index, result.destination.index);

      reorderedChildren.forEach((child, index) => {
        updateField(`${child.value}.position`, index);
      });
      handleUpdateMenuSetting(id, reorderedChildren, updateType);
    } else {
      const sourceId = result?.source?.droppableId || '';
      const destId = result?.destination?.droppableId || '';
      const sourceChildren = menus.find(item => item.id === sourceId)?.children || [];
      const destChildren = menus.find(item => item.id === destId)?.children || [];
      if (destChildren.length === MAX_ELEMENT_PER_AREA) {
        return;
      }

      const child = sourceChildren[result.source.index];
      const originalData = _.get(formValues, child.value, null);
      const isSelecting = activeMenuItem === child.value;
      updateField(child.value, ''); // Remove data setting of old place

      const [, elementName] = child.value.split('_');
      const [destElementId] = destId.split('_');
      child.value = `${destElementId}_${elementName}`;

      const [sourceResult, destResult] = reorderTwoList(
        sourceChildren,
        destChildren,
        result.source.index,
        result.destination.index
      );

      // Update data setting of new place
      updateField(child.value, originalData);
      // Update position in redux form
      sourceResult.forEach((elem, index) => {
        updateField(`${elem.value}.position`, index);
      });
      destResult.forEach((elem, index) => {
        updateField(`${elem.value}.position`, index);
      });

      // If that menu item is selecting, trigger select with new prefix to re-render form on the right panel
      if (isSelecting) {
        handleChangeActiveMenuItem(child.value, updateType);
      }

      handleUpdateMenuSetting(sourceId, sourceResult, updateType);
      handleUpdateMenuSetting(destId, destResult, updateType);
    }
  };

  // Toggle hide element
  handleToggleHideElement = item => {
    const { id, updateType, children, updateField, handleUpdateMenuSetting } = this.props;
    const cloneChildren = _.cloneDeep(children);
    const clickedElement = cloneChildren.find(child => child.value === item.value);
    clickedElement.isHidden = !clickedElement.isHidden;

    cloneChildren.forEach(child => {
      updateField(`${child.value}.isHidden`, child.isHidden);
    });
    handleUpdateMenuSetting(id, cloneChildren, updateType);
  };

  // Toggle hide menu item (collection header, search header)
  toggleHideMenuItem = (id, updateType) => {
    const { updateField, isHidden, handleToggleHideMenuItem } = this.props;
    updateField(`${id}.isHidden`, !isHidden);
    handleToggleHideMenuItem(id, updateType);
  };

  handleRemoveElement = element => {
    const {
      formValues,
      removeCurrentElement,
      updateField,
      themeName,
      updateType,
      upsertElementActions
    } = this.props;
    const elementValue = element?.value || ''; // path from formValues

    if (_.get(formValues, elementValue, null)) {
      updateField(elementValue, '');
    }

    const [prefix] = elementValue.split('_');
    let groupOfKeys = Object.keys(formValues).filter(key => {
      const [, elementKey] = key.split('_');
      return (
        key.includes(prefix) &&
        !elementKey.includes('setting') &&
        !_.isEmpty(formValues[key]) &&
        key !== elementValue
      );
    });
    groupOfKeys = _.orderBy(groupOfKeys, key => formValues[key].position, 'asc');
    groupOfKeys.forEach((key, index) => {
      updateField(`${key}.position`, index);
    });

    removeCurrentElement(elementValue, updateType);
    const removeElement = elementValue?.split('_');
    upsertElementActions('remove', {
      feature: 'Theme Settings',
      selectedTheme: themeName,
      page: removeElement[0],
      elementName: removeElement[0] + '_' + removeElement[1]
    });
  };

  toggleDragging = (key, value) => {
    this.setState({
      [key]: value
    });
  };

  renderChildren = () => {
    const {
      formValues,
      id,
      isHasAreaChildren,
      activeMenuItem,
      children,
      updateType,
      menus,
      elements,
      notDragDrop,
      updateField,
      themeSettingDefaultValue,
      themeSettingDefaultThemeValue,
      themeName,
      handleChangeActiveMenuItem,
      handleUpdateMenuSetting,
      handleToggleHideMenuItem,
      upsertElementActions,
      removeCurrentElement
    } = this.props;

    if (!isHasAreaChildren) {
      return (
        <DragDropContext onDragEnd={this.onDragEnd} onDragStart={this.onDragStartProductInfo}>
          <Droppable droppableId={`droppable-${id}`}>
            {(provided, snapshot) => {
              // const style = snapshot.isDraggingOver
              //   ? {
              //       pointerEvents: 'none'
              //     }
              //   : {};

              return (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  // style={style}
                  className="theme-setting-menu-droppable">
                  {children.map((item, index) => (
                    <Draggable
                      key={item.value}
                      draggableId={item.value}
                      index={index}
                      isDragDisabled={notDragDrop}>
                      {(provided, snapshot) => {
                        const isDragging = snapshot.isDragging;
                        const childrenClass = classnames('theme-setting-menu-children', {
                          disabled: item.isHidden,
                          active: item.value === activeMenuItem
                        });
                        const actionClass = classnames('theme-setting-menu-children__action', {
                          show: isDragging
                        });

                        return (
                          <div>
                            {this.optionalPortal(
                              provided.draggableProps.style,
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={getItemStyle(isDragging, provided.draggableProps.style)}
                                className={childrenClass}
                                onClick={() => this.handleSelectMenuItem(item.value)}>
                                <span>{item.label}</span>
                                <div className={actionClass}>
                                  <span
                                    className="btn-remove"
                                    onClick={e => {
                                      e.stopPropagation();
                                      this.handleRemoveElement(item);
                                    }}>
                                    <Icon source={DeleteMinor} color="base" />
                                  </span>
                                  <span
                                    className="btn-view"
                                    onClick={e => {
                                      e.stopPropagation();
                                      this.handleToggleHideElement(item);
                                    }}>
                                    <Icon
                                      source={item.isHidden ? HideMinor : ViewMinor}
                                      color="base"
                                    />
                                  </span>
                                  <span
                                    className="btn-drag"
                                    {...provided.dragHandleProps}
                                    style={notDragDrop ? { visibility: 'hidden' } : null}>
                                    <Icon source={DragHandleMinor} color="base" />
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>
      );
    } else {
      return (
        <DragDropContext onDragEnd={this.onDragEndMultiList} onDragStart={this.onDragStart}>
          {children.map(item => {
            return (
              <MenuItemArea
                id={item.id}
                key={item.id}
                title={item.title}
                elements={elements}
                icon={item.icon}
                activeMenuItem={activeMenuItem}
                updateType={updateType} // Type of menu (collection | search | common)
                children={item.children}
                menus={menus}
                isAddable={item.isAddable}
                updateField={updateField}
                formValues={formValues}
                themeSettingDefaultValue={themeSettingDefaultValue}
                themeSettingDefaultThemeValue={themeSettingDefaultThemeValue}
                handleChangeActiveMenuItem={handleChangeActiveMenuItem}
                handleUpdateMenuSetting={handleUpdateMenuSetting}
                handleToggleHideMenuItem={handleToggleHideMenuItem}
                upsertElementActions={upsertElementActions}
                themeName={themeName}
                removeCurrentElement={removeCurrentElement}
              />
            );
          })}
        </DragDropContext>
      );
    }
  };

  render() {
    const {
      i18n,
      title,
      icon,
      isCollapsible,
      isAddable,
      id,
      activeMenuItem,
      elements,
      isBorder,
      children,
      isAllowHide,
      updateType,
      isHidden
    } = this.props;
    const { isOpenCollapse, isOpenMenuAdd, filteredElements, isDragging, isDraggingProductInfo } =
      this.state;

    const menuItemClass = classnames('theme-setting-menu-item', {
      borderless: !isBorder
    });
    const caretClass = classnames('caret', { closed: !isOpenCollapse });
    const menuTitleClass = classnames('menu-title');
    const titleClass = classnames('title-wrapper', {
      active: id === activeMenuItem,
      'not-collapsible': !isCollapsible,
      disabled: isHidden
    });
    const btnAddClass = classnames('theme-setting-menu-children btn-add', {
      disabled: _.isEmpty(filteredElements)
    });
    const draggableZoneClass = classnames('draggable-zone', {
      dragging: isDragging || isDraggingProductInfo,
      'draggable-zone--margin-left': id === MENU_ID.TOOLBAR || id === MENU_ID.COLLECTION_HEADER
    });

    return (
      <div className={menuItemClass}>
        <div className={titleClass}>
          {isCollapsible && (
            <span className={caretClass} onClick={this.handleToggleCollapse}>
              <Icon source={CaretDownMinor} color="base" />
            </span>
          )}
          <div className="title-inner" onClick={() => this.handleSelectMenuItem(id)}>
            <span className="menu-icon">{icon}</span>
            <span className={menuTitleClass}>{i18n.translate(title)}</span>
          </div>
          {isAllowHide && (
            <span
              className="btn-view-by-page"
              onClick={e => {
                e.stopPropagation();
                this.toggleHideMenuItem(id, updateType);
              }}>
              <Icon source={isHidden ? HideMinor : ViewMinor} color="base" />
            </span>
          )}
        </div>

        {isCollapsible && (
          <div className={draggableZoneClass}>
            <Collapse isOpened={isOpenCollapse}>
              {!!children?.length && this.renderChildren()}

              {isAddable && !_.isEmpty(elements) && (
                <Popover
                  active={isOpenMenuAdd}
                  activator={
                    <div className={btnAddClass} onClick={this.handleTogglePopover}>
                      <Icon source={CirclePlusMajor} color="base" />
                      <span>{i18n.translate('AdminHTML.ThemeSetting.Common.addElement')}</span>
                    </div>
                  }
                  autofocusTarget="none"
                  preferredAlignment="right"
                  preferredPosition="mostSpace"
                  onClose={this.handleTogglePopover}>
                  <div className="theme-setting-menu-children__menu-add">
                    <ActionList
                      actionRole="menu-elements"
                      sections={[
                        {
                          title: i18n.translate('AdminHTML.ThemeSetting.Elements.title'),
                          items: filteredElements.map(item => ({
                            content: i18n.translate(item.label),
                            onAction: () => this.handleAddElement(item)
                          }))
                        }
                      ]}
                    />
                  </div>
                </Popover>
              )}
            </Collapse>
          </div>
        )}
      </div>
    );
  }
}

export default withI18n()(MenuItem);

MenuItem.defaultProps = {
  menus: []
};
